import { useState, useRef } from 'react';
import { AVATARS, AVATAR_SVG_MAP } from './avatars';
import { getCrystalGuide } from './crystalGlobeData';
import CrystalGlobe, { CrystalBodyOnly } from './CrystalGlobe';
import type { GuideCustomization } from './guideCatalogue';

export type { GuideCustomization };

interface SparkleParticle {
  id: number;
  x: number;
  y: number;
  size: number;
  color: string;
}

interface GuideBubbleProps {
  avatarId: string | null;
  size?: number;
  showGlow?: boolean;
  style?: React.CSSProperties;
  onClick?: () => void;
  ariaLabel?: string;
  customisation?: GuideCustomization | null;
}

export default function GuideBubble({
  avatarId,
  size = 60,
  showGlow = true,
  style,
  onClick,
  ariaLabel,
  customisation,
}: GuideBubbleProps) {
  const avatar = AVATARS.find(a => a.id === avatarId);
  const isCrystal = avatar?.isCrystal;
  const guide = avatarId && isCrystal ? getCrystalGuide(avatarId) : undefined;
  const SvgComponent = avatarId && !isCrystal ? AVATAR_SVG_MAP[avatarId] : undefined;
  const [sparkles, setSparkles] = useState<SparkleParticle[]>([]);
  const [bouncing, setBouncing] = useState(false);
  const sparkleIdRef = useRef(0);

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    (window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
     document.documentElement.classList.contains('reduced-motion'));

  function handleTap() {
    if (!prefersReducedMotion) {
      setBouncing(true);
      setTimeout(() => setBouncing(false), 400);

      const colors = ['var(--color-warning)', '#f472b6', 'var(--color-accent)', 'var(--color-success)', '#60a5fa', '#c084fc'];
      const newSparkles: SparkleParticle[] = Array.from({ length: 8 }, () => ({
        id: sparkleIdRef.current++,
        x: 50 + (Math.random() - 0.5) * 80,
        y: 50 + (Math.random() - 0.5) * 80,
        size: 3 + Math.random() * 5,
        color: colors[Math.floor(Math.random() * colors.length)],
      }));
      setSparkles(prev => [...prev, ...newSparkles]);
      setTimeout(() => {
        setSparkles(prev => prev.filter(s => !newSparkles.find(ns => ns.id === s.id)));
      }, 700);
    }
    onClick?.();
  }

  const defaultAnim = prefersReducedMotion ? 'none' : 'avatarFloat 3s ease-in-out infinite, avatarCalmPulse 4s ease-in-out infinite';

  const glowStyle: React.CSSProperties = showGlow && avatar
    ? { boxShadow: `0 0 ${size * 0.5}px ${avatar.bg}55` }
    : {};

  // Large crystal: render full globe (no circular container)
  if (guide && size > 80) {
    return (
      <div
        style={{ position: 'relative', cursor: onClick ? 'pointer' : 'default', ...style }}
        onClick={handleTap}
        role={onClick ? 'button' : undefined}
        aria-label={ariaLabel}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleTap(); } } : undefined}
      >
        <CrystalGlobe
          guide={guide}
          auraId={customisation?.auraId ?? 'none'}
          equippedItems={customisation?.equippedItems}
          size={size}
          reducedMotion={prefersReducedMotion}
        />
        {sparkles.map(spark => (
          <div
            key={spark.id}
            style={{
              position: 'absolute',
              left: `calc(${spark.x}% - ${spark.size / 2}px)`,
              top: `calc(${spark.y}% - ${spark.size / 2}px)`,
              width: spark.size,
              height: spark.size,
              borderRadius: '50%',
              background: spark.color,
              animation: 'sparkleFloat 0.7s ease-out forwards',
              ['--spark-dx' as string]: `${(Math.random() - 0.5) * 40}px`,
              ['--spark-dy' as string]: `${-10 - Math.random() * 30}px`,
              pointerEvents: 'none',
            }}
          />
        ))}
      </div>
    );
  }

  // Small crystal or emoji avatar: circular bubble
  const bubbleBg = guide ? `${guide.accent}22` : avatar ? `${avatar.bg}22` : 'var(--color-border)';
  const bubbleBorder = guide ? `${guide.accent}55` : avatar ? avatar.bg : 'var(--color-accent-light)';

  const containerStyle: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: '50%',
    flexShrink: 0,
    background: bubbleBg,
    border: `${Math.max(2, size * 0.04)}px solid ${bubbleBorder}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    cursor: onClick ? 'pointer' : 'default',
    transition: 'box-shadow 0.3s, border-color 0.3s',
    position: 'relative',
    ...glowStyle,
    animation: bouncing && !prefersReducedMotion ? 'avatarBounce 0.4s ease' : defaultAnim,
    ...style,
  };

  return (
    <div
      style={containerStyle}
      onClick={handleTap}
      role={onClick ? 'button' : undefined}
      aria-label={ariaLabel}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleTap(); } } : undefined}
    >
      {guide ? (
        <CrystalBodyOnly
          guide={guide}
          auraId={customisation?.auraId ?? 'none'}
          size={size * 0.92}
          faceCloseUp
        />
      ) : SvgComponent ? (
        <SvgComponent size={size * 0.92} />
      ) : avatar ? (
        <span style={{ fontSize: size * 0.44, lineHeight: 1 }} aria-hidden="true">{avatar.emoji}</span>
      ) : (
        <span style={{ fontSize: size * 0.4, lineHeight: 1 }} aria-hidden="true">🧵</span>
      )}

      {sparkles.map(spark => (
        <div
          key={spark.id}
          style={{
            position: 'absolute',
            left: `calc(${spark.x}% - ${spark.size / 2}px)`,
            top: `calc(${spark.y}% - ${spark.size / 2}px)`,
            width: spark.size,
            height: spark.size,
            borderRadius: '50%',
            background: spark.color,
            animation: 'sparkleFloat 0.7s ease-out forwards',
            ['--spark-dx' as string]: `${(Math.random() - 0.5) * 40}px`,
            ['--spark-dy' as string]: `${-10 - Math.random() * 30}px`,
            pointerEvents: 'none',
          }}
        />
      ))}
    </div>
  );
}
